/**
 * Potato Pirates simulator — pure game engine.
 *
 * Framework-free game model + interpreter. The UI builds a `GameState`, calls
 * `runSimulation` to get an ordered `SimEvent[]` log, then replays that log with
 * animation. No React/DOM here so the rules stay unit-testable.
 *
 * Rules (see plan): each player has 2 ships of 10 potatoes; up to 3 cards stack
 * under each ship. Action cards deal damage; control cards wrap actions.
 */

export type ActionType = 'roast' | 'mash' | 'fry'
export type ControlType = 'for' | 'while' | 'ifelse'
export type CardType = ActionType | ControlType

/** Potatoes destroyed per hit by each action card. */
export const ACTION_DAMAGE: Record<ActionType, number> = {
  roast: 1,
  mash: 2,
  fry: 3,
}

/** Allowed parameter values per control card (first entry is the default). */
export const CONTROL_PARAMS: Record<ControlType, number[]> = {
  for: [2, 3], // repeat count
  while: [4, 5, 6], // repeat while target potatoes > param
  ifelse: [3, 4, 5], // threshold: <= param → IF branch, else → ELSE branch
}

/** Hard cap so a malformed while-loop can never hang the UI. */
export const WHILE_GUARD = 100

export interface ActionCard {
  id: string
  kind: 'action'
  action: ActionType
  /** Selected opponent ship to hit; null = auto-pick first living opponent ship. */
  targetShipId?: string | null
}

export interface ControlCard {
  id: string
  kind: 'control'
  control: ControlType
  param: number
  /**
   * for / while body: up to 2 cards, each an action or nested for/while.
   * If-else is not allowed inside a for/while body.
   */
  body?: Card[]
  /**
   * ifelse branches hold 0–2 items each. Items may be ActionCards OR
   * For/While ControlCards (enabling "while > 4 → fry" inside an if/else
   * branch). Branches are mutually exclusive content types — either all
   * actions, or a single control card. Combined slot cost (1 per card, +1
   * for a for/while body) must be ≤ 2 so the whole construct stays within
   * the 3-card-per-ship limit (1 if/else + up to 2 nested).
   */
  ifBody?: Card[]
  elseBody?: Card[]
}

export type Card = ActionCard | ControlCard

export interface Ship {
  id: string
  name: string
  potatoes: number
  maxPotatoes: number
  ownerId: string
  /** Up to 3 cards, resolved top-to-bottom. */
  program: Card[]
}

export interface Fleet {
  id: string
  name: string
  ships: Ship[]
}

export interface GameState {
  fleets: Fleet[]
}

export type SimEventType = 'attack' | 'skip' | 'sink' | 'win'

export interface SimEvent {
  type: SimEventType
  message: string
  sourceShipId?: string
  sourceCardId?: string
  action?: ActionType
  targetShipId?: string
  damage?: number
  targetPotatoesBefore?: number
  targetPotatoesAfter?: number
  /** Snapshot of every ship's potato count immediately after this event. */
  snapshot: Record<string, number>
}

export interface SimulationResult {
  events: SimEvent[]
  finalState: GameState
  /** Did the attacker wipe out the defender fleet? */
  attackerWon: boolean
  potatoesDestroyed: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function cloneState(state: GameState): GameState {
  return {
    fleets: state.fleets.map((f) => ({
      ...f,
      ships: f.ships.map((s) => ({ ...s, program: s.program.map(cloneCard) })),
    })),
  }
}

function cloneCard(card: Card): Card {
  if (card.kind === 'action') return { ...card }
  return {
    ...card,
    body: (card.body ?? []).map(cloneCard),
    ifBody: (card.ifBody ?? []).map(cloneCard),
    elseBody: (card.elseBody ?? []).map(cloneCard),
  }
}

/** Recursive slot cost of a single card. */
function cardSlots(card: Card): number {
  if (card.kind === 'action') return 1
  if (card.control !== 'ifelse') {
    // for/while: 1 + cost of each body item
    return 1 + (card.body ?? []).reduce((s: number, c: Card) => s + cardSlots(c), 0)
  }
  // ifelse: 1 + cost of both branches
  const branchCost = (b: Card[]) => b.reduce((s, c) => s + cardSlots(c), 0)
  return 1 + branchCost(card.ifBody ?? []) + branchCost(card.elseBody ?? [])
}

/** Count card slots used by a ship program (if/else branch items count toward the 3-slot limit). */
export function countProgramSlots(program: Card[]): number {
  return program.reduce((n, card) => n + cardSlots(card), 0)
}

export function isShipDead(ship: Ship): boolean {
  return ship.potatoes <= 0
}

export function isFleetDefeated(fleet: Fleet): boolean {
  return fleet.ships.every(isShipDead)
}

function allShips(state: GameState): Ship[] {
  return state.fleets.flatMap((f) => f.ships)
}

function findShip(state: GameState, shipId: string): Ship | undefined {
  return allShips(state).find((s) => s.id === shipId)
}

function snapshotOf(state: GameState): Record<string, number> {
  const snap: Record<string, number> = {}
  for (const ship of allShips(state)) snap[ship.id] = ship.potatoes
  return snap
}

/** Resolve which defender ship an action should hit. */
function resolveTarget(
  defender: Fleet,
  preferredId: string | null | undefined,
): Ship | null {
  if (preferredId) {
    const preferred = defender.ships.find((s) => s.id === preferredId)
    if (preferred && !isShipDead(preferred)) return preferred
  }
  return defender.ships.find((s) => !isShipDead(s)) ?? null
}

// ---------------------------------------------------------------------------
// Interpreter
// ---------------------------------------------------------------------------

interface RunContext {
  state: GameState
  defender: Fleet
  events: SimEvent[]
  destroyed: number
}

/** Apply one action card to a specific ship, recording an event. */
function applyAction(
  ctx: RunContext,
  source: Ship,
  card: ActionCard,
  target: Ship,
): void {
  const action = card.action
  const damage = ACTION_DAMAGE[action]
  const before = target.potatoes
  const after = Math.max(0, before - damage)
  const dealt = before - after
  target.potatoes = after
  ctx.destroyed += dealt

  ctx.events.push({
    type: 'attack',
    sourceShipId: source.id,
    sourceCardId: card.id,
    action,
    targetShipId: target.id,
    damage: dealt,
    targetPotatoesBefore: before,
    targetPotatoesAfter: after,
    message: `${source.name} plays ${action.toUpperCase()} on ${target.name} (−${dealt} potato${dealt === 1 ? '' : 'es'})`,
    snapshot: snapshotOf(ctx.state),
  })

  if (after === 0 && before > 0) {
    ctx.events.push({
      type: 'sink',
      targetShipId: target.id,
      message: `${target.name} is destroyed! 🥔💥`,
      snapshot: snapshotOf(ctx.state),
    })
  }
}

function pushSkip(ctx: RunContext, source: Ship, message: string): void {
  ctx.events.push({ type: 'skip', sourceShipId: source.id, message, snapshot: snapshotOf(ctx.state) })
}

/**
 * Execute a branch item (action or for/while) against a pre-selected target ship.
 * Used by the ifelse interpreter — the target is always the ship being evaluated,
 * regardless of any targetShipId on the action cards inside.
 */
function runBranchItem(ctx: RunContext, source: Ship, item: Card, targetShip: Ship): void {
  if (isShipDead(targetShip)) return

  if (item.kind === 'action') {
    applyAction(ctx, source, item, targetShip)
    return
  }

  switch (item.control) {
    case 'for': {
      const body = item.body ?? []
      if (body.length === 0) {
        pushSkip(ctx, source, `${source.name}: empty FOR in branch skipped`)
        return
      }
      for (let i = 0; i < item.param; i++) {
        if (isShipDead(targetShip)) break
        for (const bodyItem of body) {
          if (isShipDead(targetShip)) break
          runBranchItem(ctx, source, bodyItem, targetShip)
        }
      }
      return
    }
    case 'while': {
      const body = item.body ?? []
      if (body.length === 0) {
        pushSkip(ctx, source, `${source.name}: empty WHILE in branch skipped`)
        return
      }
      let guard = 0
      while (guard++ < WHILE_GUARD) {
        if (isShipDead(targetShip) || targetShip.potatoes <= item.param) break
        for (const bodyItem of body) {
          if (isShipDead(targetShip)) break
          runBranchItem(ctx, source, bodyItem, targetShip)
        }
      }
      return
    }
    case 'ifelse':
      pushSkip(ctx, source, `${source.name}: nested IF/ELSE not supported`)
      return
  }
}

/** Resolve a single card in a ship's program. */
function runCard(ctx: RunContext, source: Ship, card: Card): void {
  if (card.kind === 'action') {
    const target = resolveTarget(ctx.defender, card.targetShipId)
    if (!target) {
      pushSkip(ctx, source, `${source.name}: no enemy ship left to attack`)
      return
    }
    applyAction(ctx, source, card, target)
    return
  }

  // Control cards
  switch (card.control) {
    case 'for': {
      const body = card.body ?? []
      if (body.length === 0) {
        pushSkip(ctx, source, `${source.name}: empty FOR card skipped`)
        return
      }
      for (let i = 0; i < card.param; i++) {
        if (isFleetDefeated(ctx.defender)) break
        for (const item of body) {
          if (isFleetDefeated(ctx.defender)) break
          if (item.kind === 'action') {
            const target = resolveTarget(ctx.defender, item.targetShipId)
            if (!target) { pushSkip(ctx, source, `${source.name}: no enemy left for FOR`); break }
            applyAction(ctx, source, item, target)
          } else {
            runCard(ctx, source, item)
          }
        }
      }
      return
    }
    case 'while': {
      const body = card.body ?? []
      if (body.length === 0) {
        pushSkip(ctx, source, `${source.name}: empty WHILE card skipped`)
        return
      }
      // Condition target: first action card's preferred target, or auto-resolved.
      const firstAction = body.find((c): c is ActionCard => c.kind === 'action')
      let guard = 0
      while (guard++ < WHILE_GUARD) {
        const condTarget = resolveTarget(ctx.defender, firstAction?.targetShipId ?? null)
        if (!condTarget || condTarget.potatoes <= card.param) break
        for (const item of body) {
          if (isFleetDefeated(ctx.defender)) break
          if (item.kind === 'action') {
            const target = resolveTarget(ctx.defender, item.targetShipId)
            if (!target) break
            applyAction(ctx, source, item, target)
          } else {
            runCard(ctx, source, item)
          }
        }
        if (isFleetDefeated(ctx.defender)) break
      }
      return
    }
    case 'ifelse': {
      // Evaluated against every living ship EXCEPT the attacker's own ships.
      for (const ship of allShips(ctx.state)) {
        if (isShipDead(ship)) continue
        if (ship.ownerId === source.ownerId) continue
        const inIf = (ship.potatoes ?? 0) <= card.param
        const branch: Card[] = inIf ? (card.ifBody ?? []) : (card.elseBody ?? [])
        if (branch.length === 0) {
          pushSkip(
            ctx,
            source,
            `${source.name}: IF/ELSE has no ${inIf ? 'IF' : 'ELSE'} action for ${ship.name}`,
          )
          continue
        }
        for (const item of branch) {
          if (isShipDead(ship)) break
          runBranchItem(ctx, source, item, ship)
        }
      }
      return
    }
  }
}

/**
 * Resolve `attackerId`'s ship programs against `defenderId`. Pure: deep-clones
 * the input state and returns the new state plus the event log.
 */
export function runSimulation(
  inputState: GameState,
  attackerId: string,
  defenderId: string,
): SimulationResult {
  const state = cloneState(inputState)
  const attacker = state.fleets.find((f) => f.id === attackerId)
  const defender = state.fleets.find((f) => f.id === defenderId)

  if (!attacker || !defender) {
    return { events: [], finalState: state, attackerWon: false, potatoesDestroyed: 0 }
  }

  const ctx: RunContext = { state, defender, events: [], destroyed: 0 }

  for (const ship of attacker.ships) {
    if (isShipDead(ship)) continue
    for (const card of ship.program) {
      runCard(ctx, ship, card)
      if (isFleetDefeated(defender)) break
    }
    if (isFleetDefeated(defender)) break
  }

  const attackerWon = isFleetDefeated(defender)
  if (attackerWon) {
    ctx.events.push({
      type: 'win',
      message: `${attacker.name} wins — enemy fleet destroyed! 🏴‍☠️`,
      snapshot: snapshotOf(state),
    })
  }

  return { events: ctx.events, finalState: state, attackerWon, potatoesDestroyed: ctx.destroyed }
}

// ---------------------------------------------------------------------------
// Multi-player battle
// ---------------------------------------------------------------------------

export interface MultiPlayerResult {
  events: SimEvent[]
  finalState: GameState
  winnerId: string | null
  winnerName: string | null
  potatoesDestroyed: number
}

/**
 * Run each fleet's ships in order against all other fleets. Target resolution
 * uses each card's explicit `targetShipId`; if null or dead, auto-picks the
 * first living ship not owned by the attacker. `ifelse` hits all enemy ships
 * (all ships except the attacker's own fleet).
 */
export function runMultiPlayerBattle(inputState: GameState): MultiPlayerResult {
  const state = cloneState(inputState)
  const allEvts: SimEvent[] = []
  let totalDestroyed = 0

  for (const fleet of state.fleets) {
    if (isFleetDefeated(fleet)) continue

    // Virtual defender = all ships from all other fleets, in fleet order.
    const otherShips = state.fleets
      .filter((f) => f.id !== fleet.id)
      .flatMap((f) => f.ships)
    const virtualDefender: Fleet = { id: '__others__', name: 'Others', ships: otherShips }
    const ctx: RunContext = { state, defender: virtualDefender, events: [], destroyed: 0 }

    for (const ship of fleet.ships) {
      if (isShipDead(ship)) continue
      for (const card of ship.program) {
        runCard(ctx, ship, card)
      }
    }

    allEvts.push(...ctx.events)
    totalDestroyed += ctx.destroyed

    if (state.fleets.filter((f) => !isFleetDefeated(f)).length <= 1) break
  }

  const living = state.fleets.filter((f) => !isFleetDefeated(f))
  let winnerId: string | null = null
  let winnerName: string | null = null

  if (living.length === 1) {
    winnerId = living[0].id
    winnerName = living[0].name
    allEvts.push({
      type: 'win',
      message: `${living[0].name} wins — all enemies destroyed! 🏴‍☠️`,
      snapshot: snapshotOf(state),
    })
  } else if (living.length > 1) {
    // No full elimination — most potatoes remaining wins.
    const sorted = [...living].sort(
      (a, b) =>
        b.ships.reduce((s, sh) => s + sh.potatoes, 0) -
        a.ships.reduce((s, sh) => s + sh.potatoes, 0),
    )
    winnerId = sorted[0].id
    winnerName = sorted[0].name
  }

  return { events: allEvts, finalState: state, winnerId, winnerName, potatoesDestroyed: totalDestroyed }
}

// ---------------------------------------------------------------------------
// State factories
// ---------------------------------------------------------------------------

let _idCounter = 0
/** Deterministic id generator (Math.random is unavailable in some sandboxes). */
export function nextId(prefix = 'id'): string {
  _idCounter += 1
  return `${prefix}_${_idCounter}`
}

export function makeShip(ownerId: string, name: string, potatoes = 10): Ship {
  return {
    id: nextId('ship'),
    name,
    potatoes,
    maxPotatoes: Math.max(potatoes, 10),
    ownerId,
    program: [],
  }
}

export function makeFleet(id: string, name: string, shipPotatoes: number[] = [10, 10]): Fleet {
  return {
    id,
    name,
    ships: shipPotatoes.map((p, i) => makeShip(id, `${name} Ship ${i + 1}`, p)),
  }
}

/** Single-player: player fleet (editable) vs a fixed opponent fleet. */
export function makeSinglePlayerState(
  opponentPotatoes: number[] = [10, 10],
  playerPotatoes: number[] = [10, 10],
): GameState {
  return {
    fleets: [makeFleet('player', 'Your', playerPotatoes), makeFleet('opponent', 'Enemy', opponentPotatoes)],
  }
}

/** Two-player hotseat: both fleets editable. */
export function makeTwoPlayerState(): GameState {
  return {
    fleets: [makeFleet('player_a', 'Player 1', [10, 10]), makeFleet('player_b', 'Player 2', [10, 10])],
  }
}

/** N-player sandbox (2–6): all fleets editable with explicit target selectors. */
export function makeMultiPlayerState(playerCount: number): GameState {
  return {
    fleets: Array.from({ length: playerCount }, (_, i) =>
      makeFleet(`player_${i + 1}`, `Player ${i + 1}`, [10, 10]),
    ),
  }
}
